// client/src/components/Croquettes.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";

// -----------------------------
// Constantes + helpers (identiques à Settings pour rester autonome)
// -----------------------------
const PROTEIN_SOURCES = ["poulet", "poisson", "agneau", "boeuf", "dinde", "canard", "insectes", "porc", "oeuf"];

const FUNCTIONAL_BENEFITS = [
  { key: "digestion", label: "Digestion / transit" },
  { key: "probiotics", label: "Probiotiques / prébiotiques" },
  { key: "skin_coat", label: "Peau & pelage (oméga 3/6)" },
  { key: "joints", label: "Articulations (glucosamine/chondroïtine)" },
  { key: "immunity", label: "Immunité" },
  { key: "weight", label: "Gestion du poids" },
  { key: "stress_relax", label: "Relax / anti-stress" },
  { key: "dental", label: "Dentaire" },
];

function uniq(arr) {
  return Array.from(new Set((arr || []).filter(Boolean)));
}

function clampNumber(n, min, max) {
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function getSizeKeyFromAdultWeight(adultKg) {
  if (!adultKg || adultKg <= 0) return "all";
  if (adultKg < 5) return "xs";
  if (adultKg < 10) return "s";
  if (adultKg < 25) return "m";
  if (adultKg < 45) return "l";
  return "xl";
}

function getLifeStageFromAgeMonths(ageMonths) {
  if (!ageMonths || ageMonths <= 0) return "adult";
  if (ageMonths < 12) return "puppy";
  if (ageMonths >= 84) return "senior";
  return "adult";
}

function scoreProductForDog(product, dog) {
  let score = 0;
  const reasons = [];

  const dogLife = getLifeStageFromAgeMonths(dog.ageMonths);
  const dogSize = getSizeKeyFromAdultWeight(dog.adultWeightKg || dog.currentWeightKg);

  // 1) Life stage
  if ((product.lifeStages || []).includes("all") || (product.lifeStages || []).includes(dogLife)) {
    score += 30;
    reasons.push(`Âge OK (${dogLife})`);
  } else {
    score -= 40;
    reasons.push(`Âge pas idéal (${dogLife})`);
  }

  // 2) Taille
  if ((product.sizeTargets || []).includes("all") || (product.sizeTargets || []).includes(dogSize)) {
    score += 20;
    reasons.push(`Taille OK (${dogSize})`);
  } else {
    score -= 15;
    reasons.push(`Taille pas idéale (${dogSize})`);
  }

  // 3) Stérilisé / poids
  if (dog.sterilised) {
    if (product.sterilisedSuitable) {
      score += 15;
      reasons.push("Convient stérilisé");
    } else {
      score -= 8;
      reasons.push("Pas orienté stérilisé");
    }
    if (dog.weightGoal && product.weightControl) {
      score += 10;
      reasons.push("Gestion du poids");
    }
  }

  // 4) Hypo
  if (dog.hypoallergenicNeed) {
    if (product.hypoallergenic) {
      score += 20;
      reasons.push("Hypoallergénique");
    } else {
      score -= 20;
      reasons.push("Pas hypoallergénique");
    }
  }

  // Allergies protéines
  const dogAllergies = uniq(dog.allergies || []);
  const productProteins = uniq(product.proteinSources || []).concat(product.primaryProtein ? [product.primaryProtein] : []);
  const hitProteinAllergy = productProteins.some((p) => dogAllergies.includes(p));
  if (hitProteinAllergy) {
    score -= 80;
    reasons.push("⚠️ Contient une protéine allergène");
  }

  // Exclusions gluten/ble/mais/soja
  const productExcl = uniq(product.exclusions || []);
  const hitExclusion = dogAllergies.some((a) => {
    if (["gluten", "ble", "mais", "soja"].includes(a)) return !productExcl.includes(a);
    return false;
  });
  if (hitExclusion) {
    score -= 25;
    reasons.push("⚠️ Exclusion demandée non garantie");
  }

  // Besoins fonctionnels
  const wantedBenefits = uniq(dog.needs || []);
  const prodBenefits = uniq(product.benefits || []);
  wantedBenefits.forEach((need) => {
    if (prodBenefits.includes(need)) {
      score += 12;
      reasons.push(`Couvre besoin: ${need}`);
    }
  });

  // Préférence protéine
  if (dog.preferredProtein) {
    if (product.primaryProtein === dog.preferredProtein) {
      score += 8;
      reasons.push("Protéine principale préférée");
    } else if (productProteins.includes(dog.preferredProtein)) {
      score += 4;
      reasons.push("Protéine préférée présente");
    }
  }

  // Activité
  if (dog.activityLevel === "high") {
    if (product.weightControl) score -= 4;
  } else if (dog.activityLevel === "low") {
    if (product.weightControl) score += 5;
  }

  score = clampNumber(score, -200, 200);
  return { score, reasons };
}


export default function Croquettes() {
  // produits
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "Croquettes"), orderBy("updatedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingProducts(false);
      },
      () => setLoadingProducts(false)
    );
    return () => unsub();
  }, []);

  // simulateur
  const [dog, setDog] = useState({
    ageMonths: 24,
    currentWeightKg: 12,
    adultWeightKg: 15,
    sterilised: false,
    activityLevel: "medium",
    hypoallergenicNeed: false,
    needs: ["digestion"],
    allergies: [],
    preferredProtein: "",
    budgetMax: "",
    weightGoal: false,
  });

  function setDogField(key, value) {
    setDog((p) => ({ ...p, [key]: value }));
  }
  function toggleDogArray(key, value) {
    setDog((p) => {
      const s = new Set(p[key] || []);
      if (s.has(value)) s.delete(value);
      else s.add(value);
      return { ...p, [key]: Array.from(s) };
    });
  }

  const recommendations = useMemo(() => {
    const budgetMax = dog.budgetMax === "" ? null : Number(dog.budgetMax);

    return (products || [])
      .filter((p) => {
        if (budgetMax == null) return true;
        if (p.price == null) return true;
        return Number(p.price) <= budgetMax;
      })
      .map((p) => {
        const { score, reasons } = scoreProductForDog(p, dog);
        return { product: p, score, reasons };
      })
      .sort((a, b) => b.score - a.score);
  }, [products, dog]);

  const best = recommendations[0] || null;

  const css = `
.c-wrap{
  --bg:var(--eb-bg);
  --card:var(--eb-surface);
  --card2:var(--eb-surface);
  --stroke:var(--eb-border);
  --text:var(--eb-text);
  --muted:var(--eb-muted);
  --shadow:var(--eb-shadow-sm);
  --r:var(--eb-radius-lg);
  --r2:var(--eb-radius);
  --pad:20px;
}

.c-wrap{
  min-height:100vh;
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 50% -10%, rgba(39, 76, 116, 0.08), transparent 60%),
    var(--bg);
  padding: 26px 16px;
}

.c-container{
  max-width: var(--eb-content-max, 1240px);
  margin: 0 auto;
}

.c-topbar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.c-title{
  display:flex;
  flex-direction:column;
  gap:4px;
}

.c-title h1{
  margin:0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.c-sub{
  color:var(--muted);
  font-size: 13px;
}

.c-gear{
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  color: var(--text);
  border-radius: var(--eb-radius);
  padding: 10px 12px;
  cursor:pointer;
  display:flex;
  align-items:center;
  gap:8px;
  box-shadow: var(--shadow);
  transition: transform .12s ease, box-shadow .12s ease;
}
.c-gear:hover{
  transform: translateY(-1px);
  box-shadow: var(--eb-shadow-md);
}

.c-grid{
  display:grid;
  grid-template-columns: minmax(0,1.15fr) minmax(0,.85fr);
  gap: 20px;
  align-items: start;
}
@media (max-width: 980px){
  .c-grid{ grid-template-columns: 1fr; }
}

.c-card{
  border:1px solid var(--stroke);
  background: var(--card);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  padding: var(--pad);
}

.c-card h2{
  margin:0 0 12px 0;
  font-size: 16px;
  letter-spacing: .2px;
}

.c-row3{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 10px;
}
@media (max-width: 700px){
  .c-row3{ grid-template-columns: 1fr; }
  .c-wrap{ padding: 20px 14px 96px; }
  .c-card{ --pad:16px; }
}

.c-field label{
  display:block;
  font-size:13px;
  color: var(--muted);
  margin-bottom:6px;
}

.c-field input,
.c-field select{
  width:100%;
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  color: var(--text);
  padding: 10px 12px;
  border-radius: 12px;
  outline:none;
}

.c-field input::placeholder{
  color: var(--eb-subtle);
}

.c-pillbox{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
}

.c-pill{
  border:1px solid var(--stroke);
  background:var(--eb-surface-2);
  color: var(--text);
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 13px;
  cursor:pointer;
  user-select:none;
  transition: transform .08s ease, background .12s ease;
}

.c-pill:hover{
  transform: translateY(-1px);
  background:var(--eb-brand-50);
}

.c-pill.on{
  background:var(--eb-brand-100);
  border-color:var(--eb-brand-300);
  color:var(--eb-brand);
}

.c-check{
  display:flex;
  align-items:center;
  gap:10px;
  border:1px solid var(--stroke);
  background:var(--eb-surface-2);
  padding: 10px 12px;
  border-radius: var(--eb-radius);
}

.c-check input{ transform: scale(1.05); }

.c-muted{
  color: var(--muted);
  font-size: 13px;
}

.c-best{
  border:1px solid var(--stroke);
  background: var(--card2);
  border-radius: var(--r2);
  padding: 14px;
}

.c-bestTop{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.c-bestName{
  font-weight:700;
  font-size: 16px;
  line-height: 1.2;
}

.c-score{
  font-weight:700;
  font-size: 18px;
  padding: 8px 10px;
  border-radius: var(--eb-radius);
  border:1px solid var(--stroke);
  background:var(--eb-surface-2);
  min-width: 110px;
  text-align:center;
}

.c-list{
  margin:0;
  padding-left: 18px;
  color: rgba(15,23,42,0.85);
}

.c-rank details{
  margin-top: 10px;
  border:1px solid var(--stroke);
  border-radius: var(--r2);
  background:var(--eb-surface-2);
  padding: 10px 12px;
}

.c-rank summary{
  cursor:pointer;
  color: var(--text);
}

.c-rItem{
  border:1px solid var(--stroke);
  background:var(--eb-surface);
  border-radius: var(--eb-radius);
  padding: 10px 12px;
  margin-top: 10px;
}

.c-rItemTop{
  display:flex;
  justify-content:space-between;
  gap:10px;
  flex-wrap: wrap;
  font-weight:800;
}

.c-rMeta{
  margin-top: 6px;
  font-size: 13px;
  color: var(--muted);
}

.c-wrap input:focus, .c-wrap select:focus, .c-wrap textarea:focus{
  border-color: var(--eb-brand-500);
  box-shadow: var(--eb-ring);
}
`;


  return (
    <div className="c-wrap">
      <style>{css}</style>

      <div className="c-container">
        <div className="c-topbar">
          <div className="c-title">
            <h1>Croquettes</h1>
            <div className="c-sub">Simulation rapide + recommandations</div>
          </div>

          <button className="c-gear" onClick={() => (window.location.href = "/CroquettesSettings")} aria-label="Paramètres">
  ⚙️ Paramètres
</button>

        </div>

        <div className="c-grid">
          {/* LEFT: simulateur */}
          <section className="c-card">
            <h2>Profil du chien</h2>

            <div className="c-row3">
              <div className="c-field">
                <label>Âge (mois)</label>
                <input
                  type="number"
                  min={1}
                  value={dog.ageMonths}
                  onChange={(e) => setDogField("ageMonths", Number(e.target.value))}
                />
              </div>

              <div className="c-field">
                <label>Poids actuel (kg)</label>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={dog.currentWeightKg}
                  onChange={(e) => setDogField("currentWeightKg", Number(e.target.value))}
                />
              </div>

              <div className="c-field">
                <label>Poids adulte estimé (kg)</label>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={dog.adultWeightKg}
                  onChange={(e) => setDogField("adultWeightKg", Number(e.target.value))}
                />
              </div>
            </div>

            <div style={{ height: 10 }} />

            <div className="c-row3">
              <div className="c-check">
                <input
                  type="checkbox"
                  checked={dog.sterilised}
                  onChange={(e) => setDogField("sterilised", e.target.checked)}
                />
                <div>
                  <div style={{ fontWeight: 800 }}>Stérilisé</div>
                  <div className="c-muted">Ajuste le tri vers “weight control / stérilisé”</div>
                </div>
              </div>

              <div className="c-check">
                <input
                  type="checkbox"
                  checked={dog.hypoallergenicNeed}
                  onChange={(e) => setDogField("hypoallergenicNeed", e.target.checked)}
                />
                <div>
                  <div style={{ fontWeight: 800 }}>Besoin hypoallergénique</div>
                  <div className="c-muted">Renforce fortement les produits hypo</div>
                </div>
              </div>

              <div className="c-field">
                <label>Niveau d’activité</label>
                <select value={dog.activityLevel} onChange={(e) => setDogField("activityLevel", e.target.value)}>
                  <option value="low">Faible</option>
                  <option value="medium">Moyen</option>
                  <option value="high">Élevé</option>
                </select>
              </div>
            </div>

            <div style={{ height: 10 }} />

            <div className="c-row3">
              <div className="c-field">
                <label>Protéine préférée (optionnel)</label>
                <select value={dog.preferredProtein} onChange={(e) => setDogField("preferredProtein", e.target.value)}>
                  <option value="">—</option>
                  {PROTEIN_SOURCES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="c-field">
                <label>Budget max (optionnel)</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={dog.budgetMax}
                  onChange={(e) => setDogField("budgetMax", e.target.value)}
                  placeholder="Ex: 60"
                />
              </div>

              <div className="c-check">
                <input
                  type="checkbox"
                  checked={!!dog.weightGoal}
                  onChange={(e) => setDogField("weightGoal", e.target.checked)}
                />
                <div>
                  <div style={{ fontWeight: 800 }}>Objectif poids</div>
                  <div className="c-muted">Favorise “weight control”</div>
                </div>
              </div>
            </div>

            <div style={{ height: 14 }} />

            <div className="c-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
              <h2 style={{ marginBottom: 10 }}>Besoins</h2>
              <div className="c-pillbox">
                {FUNCTIONAL_BENEFITS.map((b) => {
                  const on = (dog.needs || []).includes(b.key);
                  return (
                    <div
                      key={b.key}
                      className={`c-pill ${on ? "on" : ""}`}
                      onClick={() => toggleDogArray("needs", b.key)}
                      role="button"
                      tabIndex={0}
                    >
                      {b.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ height: 14 }} />

            <div className="c-card" style={{ boxShadow: "none", padding: 14, background: "var(--eb-surface-2)" }}>
              <h2 style={{ marginBottom: 10 }}>Allergies / exclusions</h2>
              <div className="c-pillbox">
                {PROTEIN_SOURCES.map((p) => {
                  const on = (dog.allergies || []).includes(p);
                  return (
                    <div
                      key={p}
                      className={`c-pill ${on ? "on" : ""}`}
                      onClick={() => toggleDogArray("allergies", p)}
                      role="button"
                      tabIndex={0}
                    >
                      {p}
                    </div>
                  );
                })}
                {["gluten", "ble", "mais", "soja"].map((x) => {
                  const on = (dog.allergies || []).includes(x);
                  return (
                    <div
                      key={x}
                      className={`c-pill ${on ? "on" : ""}`}
                      onClick={() => toggleDogArray("allergies", x)}
                      role="button"
                      tabIndex={0}
                    >
                      sans {x}
                    </div>
                  );
                })}
              </div>
              <div className="c-muted" style={{ marginTop: 10 }}>
                Profil déduit : âge → <b>{getLifeStageFromAgeMonths(dog.ageMonths)}</b> • taille →{" "}
                <b>{getSizeKeyFromAdultWeight(dog.adultWeightKg || dog.currentWeightKg)}</b>
              </div>
            </div>
          </section>

          {/* RIGHT: recommandation */}
          <aside className="c-card">
            <h2>Meilleure recommandation</h2>

            {loadingProducts ? (
              <div className="c-muted">Chargement des produits…</div>
            ) : products.length === 0 ? (
              <div className="c-muted">
                Aucun produit enregistré. Va dans <b>Paramètres</b> pour en créer.
              </div>
            ) : !best ? (
              <div className="c-muted">Pas de recommandation.</div>
            ) : (
              <div className="c-best">
                <div className="c-bestTop">
                  <div>
                    <div className="c-bestName">
                      {best.product.brand} — {best.product.name}
                    </div>
                    <div className="c-muted" style={{ marginTop: 6 }}>
                      Protéine principale : <b>{best.product.primaryProtein || "—"}</b> • {best.product.bagWeightKg} kg •{" "}
                      {best.product.price != null ? `${best.product.price} ${best.product.currency || ""}` : "prix —"}
                    </div>
                  </div>

                  <div className="c-score">Score {best.score}</div>
                </div>

                <div style={{ fontWeight: 800, marginBottom: 6 }}>Pourquoi</div>
                <ul className="c-list">
                  {best.reasons.slice(0, 8).map((r, idx) => (
                    <li key={idx}>{r}</li>
                  ))}
                </ul>

                {best.product.annotation && (
                  <div style={{ marginTop: 12, border: "1px solid var(--stroke)", borderRadius: 14, padding: 12, background: "var(--eb-surface-2)" }}>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>Annotation</div>
                    <div style={{ whiteSpace: "pre-wrap", color: "var(--eb-text-2)", fontSize: 13 }}>
                      {best.product.annotation}
                    </div>
                  </div>
                )}
              </div>
            )}

            {recommendations.length > 1 && (
              <div className="c-rank">
                <details>
                  <summary>Voir le classement ({recommendations.length})</summary>
                  {recommendations.slice(0, 10).map((row) => (
                    <div key={row.product.id} className="c-rItem">
                      <div className="c-rItemTop">
                        <div>
                          {row.product.brand} — {row.product.name}
                        </div>
                        <div>Score: {row.score}</div>
                      </div>
                      <div className="c-rMeta">
                        {row.product.primaryProtein ? `Protéine: ${row.product.primaryProtein}` : "—"} •{" "}
                        {row.product.grainLevel ? `Grain: ${row.product.grainLevel}` : "—"} •{" "}
                        {row.product.lifeStages?.join(", ") || "—"} • {row.product.sizeTargets?.join(", ") || "—"}
                      </div>
                      {row.product.annotation && (
                        <div className="c-rMeta" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                          <b>Annotation :</b> {row.product.annotation}
                        </div>
                      )}
                    </div>
                  ))}
                </details>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
